import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Typography, Chip, Divider, Stack } from '@mui/material';
import { apiConstants, getGeneric, postGeneric, postMultipart } from '../../common/constants';
import { getMerchant } from '../../helpers/ReduxHelpers';
import { FormProvider, RHFTextField, RHFFileField } from '../../common/hook-form';
import RHFTextArea from '../../common/hook-form/RHFTextArea';
import withLayout from '../../hocs/Layout';
import PageTitle from '../../common/PageTitle';
import ImageCropDialog from './ImageCropDialog';
import { pageContainerSx, sectionCardSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx, badgeSx } from '../../styles/patterns';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE, BORDER, WHITE, PRIMARY_BLUE } from '../../styles/foundation/colors';
import { SHADOW } from '../../styles/foundation/effects';

// Keys mirror com.prozo.proship.enums.TrackingTemplateEnum on the backend.
const TEMPLATE_CARDS = [
  { value: 'CLASSIC', label: 'Classic' },
  { value: 'MODERN', label: 'Modern' },
  { value: 'MINIMAL', label: 'Minimal' },
];

// Origin where tracking-app (the renderer) is served — its /preview route renders a chosen
// template with branding via query params + sample shipment data. Defaults to the dev deployment
// (same as the admin panel); override with REACT_APP_TRACKING_APP_URL (e.g. http://localhost:3000
// in .env.local for a local tracking-app, or the prod tracking domain in .env.prod).
const TRACKING_APP_URL = process.env.REACT_APP_TRACKING_APP_URL || 'https://dev.d2lfb0u0mchb6n.amplifyapp.com';

// Logical viewport widths for the device preview. tracking-app's useIsMobile is <=900,
// so 390 renders the mobile layout/banner and 1280 the desktop one.
const DESKTOP_W = 1280;
const MOBILE_W = 390;

// ── Design-system helpers (mirrors the numbered sections of the Settings hub) ──
const numBadgeSx = (color) => ({
  width: 24, height: 24, borderRadius: '50%', bgcolor: color, color: WHITE,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, fontWeight: WEIGHT.bold, flexShrink: 0, fontFamily: FONT,
});
const sectionHeaderSx = { display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 };
const requiredTagSx = { ...badgeSx(PRIMARY_BLUE), fontSize: 10, lineHeight: 1.6 };
const optionalTagSx = { ...badgeSx(SUBTLE), fontSize: 10, lineHeight: 1.6 };
const cardTitleSx = { ...sectionTitleSx, fontSize: SIZE.lg, mb: 0 };
const hintSx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.5 };

// Compact, constant-height "current image" strip above each upload box — same height whether an
// image exists yet or not, so the three upload columns always line up with no stray blank space.
const UploadedThumb = ({ label, url, height = 36, onRemove }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, height: height + 8 }}>
    {url ? (
      <>
        <img src={url} alt={label} style={{ maxHeight: height, maxWidth: 140, objectFit: 'contain' }} />
        <Typography sx={hintSx}>{label}</Typography>
        {onRemove && (
          <Button size="small" color="error" sx={{ minWidth: 0, fontSize: 11, textTransform: 'none' }} onClick={onRemove}>
            Remove
          </Button>
        )}
      </>
    ) : (
      <Typography sx={hintSx}>No image uploaded yet</Typography>
    )}
  </Box>
);

const DEFAULTS = {
  designTemplate: 'CLASSIC',
  brandName: '',
  bannerBgColor: '',
  webUrl: '',
  footer: '',
  instaUrl: '',
  fbUrl: '',
  twitterUrl: '',
  linkedinUrl: '',
  whatsappNumber: '',
  termsAndConditions: '',
  returnPolicy: '',
  privacyPolicy: '',
};

/**
 * Merchant-facing DIY tracking-page configuration.
 *
 * Logo + banner images upload to VERSIONED S3 keys (tracking/{merchantOid}/logo|banner-desktop|banner-mobile-<ts>.<ext>)
 * served permanently via the wms-logos CloudFront — each upload is a fresh, fully cacheable URL, so
 * re-uploads are instantly visible (the CDN's cache policy ignores query strings, so constant keys can't be busted).
 */
const TrackingConfig = () => {
  const { enqueueSnackbar } = useSnackbar();
  // The currently-selected merchant (an internal user may be acting on behalf of one).
  const merchant = getMerchant();
  const merchantOid = merchant && merchant.id ? merchant.id : '';
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [trackingUrl, setTrackingUrl] = useState(null);
  const [status, setStatus] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [bannerLargeUrl, setBannerLargeUrl] = useState('');
  const [bannerSmallUrl, setBannerSmallUrl] = useState('');
  const [uploading, setUploading] = useState({ logo: false, large: false, small: false });
  const [previewSrc, setPreviewSrc] = useState('');
  const [device, setDevice] = useState('desktop');
  const previewWrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);

  const methods = useForm({ defaultValues: DEFAULTS });
  const { handleSubmit, reset, watch, setValue, setError } = methods;
  const designTemplate = watch('designTemplate');
  const logoFile = watch('file');
  const bannerDesktopFile = watch('bannerDesktopFile');
  const bannerMobileFile = watch('bannerMobileFile');

  // Upload one asset under a VERSIONED per-merchant key and return its permanent CDN URL.
  // uploadImagesNonExpire stores to the wms-logos bucket behind CloudFront (djslb35m04okm) whose
  // cache policy ignores query strings — so a constant key + ?v= buster would serve a stale image
  // for up to a day after a re-upload. A timestamped filename makes every upload a fresh, fully
  // cacheable URL instead (old versions just orphan in S3, which is fine).
  const uploadAsset = async (file, baseName) => {
    // Only the EXTENSION survives from the user's filename (sanitized); the key itself is always
    // our own base name, so identically-named source files can never collide across the three slots.
    const ext = ((file.name.split('.').pop() || 'png').toLowerCase().replace(/^jpeg$/, 'jpg').replace(/[^a-z0-9]/g, '') || 'png');
    const renamed = new File([file], `${baseName}-${Date.now()}.${ext}`, { type: file.type || 'image/png' });
    const fd = new FormData();
    fd.append('attachments[]', renamed);
    fd.append('folder', `tracking/${merchantOid}`);
    const up = await postMultipart('tools/uploadImagesNonExpire', fd);
    const clean = up && up.data && up.data[0];
    if (!clean) throw new Error('upload returned no url');
    return clean;
  };

  // Crop-on-select: picking a file opens the crop dialog (fixed proportions per slot, anchored to
  // the live templates: desktop banner 6:1 like the real brand strips, mobile banner 3:2, logo free
  // trim); the CROPPED image is what gets uploaded, so the stored asset always fits the placeholder
  // UI. Upload still happens before Save so the cross-origin preview iframe can show a hosted URL.
  const [pendingCrop, setPendingCrop] = useState(null); // { slot, file }
  const SLOTS = {
    logo: { field: 'file', baseName: 'logo', aspect: undefined, outWidth: 600, minWidth: 120, minHeight: 48, setUrl: setLogoUrl, title: 'Crop logo', hint: 'Trim the empty space around the logo — any shape works; pages scale it by height. Recommended 360×120px or larger.' },
    large: { field: 'bannerDesktopFile', baseName: 'banner-desktop', aspect: 6, outWidth: 1920, minWidth: 960, minHeight: 160, setUrl: setBannerLargeUrl, title: 'Crop desktop banner (6:1)', hint: 'A wide strip shown across the top of the page on desktop — drag to frame the right part. Recommended 1920×320px.' },
    small: { field: 'bannerMobileFile', baseName: 'banner-mobile', aspect: 2, outWidth: 750, minWidth: 375, minHeight: 188, setUrl: setBannerSmallUrl, title: 'Crop mobile banner (2:1)', hint: 'Shown at the top of the page on phones — drag to frame the right part. Recommended 750×375px.' },
  };
  const useCropOnSelect = (file, slot) => {
    useEffect(() => {
      const f = file && file[0];
      if (!f || !merchantOid) return;
      setPendingCrop({ slot, file: f });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);
  };
  useCropOnSelect(logoFile, 'logo');
  useCropOnSelect(bannerDesktopFile, 'large');
  useCropOnSelect(bannerMobileFile, 'small');

  const closeCrop = () => {
    if (pendingCrop) setValue(SLOTS[pendingCrop.slot].field, null);
    setPendingCrop(null);
  };
  const onCropped = (blob) => {
    const slot = SLOTS[pendingCrop.slot];
    const key = pendingCrop.slot;
    const ext = blob.type === 'image/png' ? 'png' : 'jpg';
    const cropped = new File([blob], `crop.${ext}`, { type: blob.type });
    setPendingCrop(null);
    setValue(slot.field, null);
    setUploading((u) => ({ ...u, [key]: true }));
    uploadAsset(cropped, slot.baseName)
      .then((url) => slot.setUrl(url))
      .catch(() => enqueueSnackbar(`${slot.baseName} upload failed — please try another image.`, { variant: 'warning' }))
      .finally(() => setUploading((u) => ({ ...u, [key]: false })));
  };

  // Build the tracking-app preview URL from the current form values + uploaded image URLs.
  const buildPreviewUrl = () => {
    const v = methods.getValues();
    const qs = new URLSearchParams({
      template: v.designTemplate || 'CLASSIC',
      brandName: v.brandName || '',
      logo: logoUrl || '',
      bannerLarge: bannerLargeUrl || '',
      bannerSmall: bannerSmallUrl || '',
      webUrl: v.webUrl || '',
      color: v.bannerBgColor || '',
      footer: v.footer || '',
      instaUrl: v.instaUrl || '',
      fbUrl: v.fbUrl || '',
      twitterUrl: v.twitterUrl || '',
      linkedinUrl: v.linkedinUrl || '',
      whatsappNumber: v.whatsappNumber || '',
      termsAndConditions: v.termsAndConditions || '',
      returnPolicy: v.returnPolicy || '',
      privacyPolicy: v.privacyPolicy || '',
    });
    return `${TRACKING_APP_URL}/preview?${qs.toString()}`;
  };

  // Refresh the preview when the design or any uploaded image changes; use the button for text edits.
  useEffect(() => {
    setPreviewSrc(buildPreviewUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designTemplate, logoUrl, bannerLargeUrl, bannerSmallUrl]);

  // Measure the preview container so the desktop frame can be scaled down to fit.
  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => setWrapW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    getGeneric(apiConstants.TRACKING_MY_CONFIG, { merchantOid })
      .then((res) => {
        const cfg = res && res.data;
        if (!cfg) return;
        const trackingPage = (cfg.pages || []).find((p) => p.type === 'TRACKING') || {};
        setLogoUrl(cfg.logoUrl || '');
        setBannerLargeUrl(trackingPage.bannerLarge || '');
        setBannerSmallUrl(trackingPage.bannerSmall || '');
        setTrackingUrl(trackingPage.url || null);
        setStatus(trackingPage.status || null);
        reset({
          designTemplate: trackingPage.designTemplate || 'CLASSIC',
          footer: trackingPage.footer || '',
          brandName: cfg.brandName || '',
          bannerBgColor: cfg.bannerBgColor || '',
          webUrl: cfg.webUrl || '',
          instaUrl: cfg.instaUrl || '',
          fbUrl: cfg.fbUrl || '',
          twitterUrl: cfg.twitterUrl || '',
          linkedinUrl: cfg.linkedinUrl || '',
          whatsappNumber: cfg.whatsappNumber || '',
          termsAndConditions: cfg.termsAndConditions || '',
          returnPolicy: cfg.returnPolicy || '',
          privacyPolicy: cfg.privacyPolicy || '',
        });
      })
      .catch(() => {
        /* no config yet — start from a fresh form */
      });
  }, [reload, reset, merchantOid]);

  const onSubmit = async (data) => {
    // Brand name is the only hard requirement — templates show it as text when no logo is uploaded.
    if (!data.brandName || !data.brandName.trim()) {
      setError('brandName', { type: 'required', message: 'Brand name is required' });
      enqueueSnackbar('Please fill the required fields — brand name is missing.', { variant: 'warning' });
      return;
    }
    // Saving a LIVE page changes what customers see immediately (no approval step) — confirm.
    if (status === 'LIVE') {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Your tracking page is LIVE — saving will update the live page for your customers immediately. Continue?')) {
        return;
      }
    }
    if (!logoUrl) {
      enqueueSnackbar('Please upload your brand logo — it is required for the page header.', { variant: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        brandName: data.brandName,
        logoUrl,
        bannerBgColor: data.bannerBgColor,
        webUrl: data.webUrl,
        instaUrl: data.instaUrl,
        fbUrl: data.fbUrl,
        twitterUrl: data.twitterUrl,
        linkedinUrl: data.linkedinUrl,
        whatsappNumber: data.whatsappNumber,
        termsAndConditions: data.termsAndConditions,
        returnPolicy: data.returnPolicy,
        privacyPolicy: data.privacyPolicy,
        designTemplate: data.designTemplate,
        footer: data.footer,
        bannerLarge: bannerLargeUrl,
        bannerSmall: bannerSmallUrl,
      };

      await postGeneric(`${apiConstants.TRACKING_SAVE_BRANDING}?merchantOid=${merchantOid}`, payload);
      enqueueSnackbar('Tracking page saved', { variant: 'success' });
      setReload((r) => r + 1);
    } catch (e) {
      enqueueSnackbar('Could not save tracking page. Please try again.', { variant: 'error' });
    }
    setLoading(false);
  };

  const anyUploading = uploading.logo || uploading.large || uploading.small;
  // Reachable URL for the saved subdomain. The ENVIRONMENT decides the form, not the status:
  // - prod (VANITY_ENABLED=true): https://<brand>.proship.in — the wildcard routes it to the prod
  //   tracking-app, and the ?host= override is compiled OUT of prod builds anyway.
  // - staging/qa (default false): the dev tracking-app + ?host= override — the vanity URL would hit
  //   the PROD tracking-app (via the wildcard), which doesn't serve staging configs.
  const VANITY_ENABLED = process.env.REACT_APP_TRACKING_VANITY_ENABLED === 'true';
  const publicUrl = trackingUrl
    ? ((status === 'LIVE' && VANITY_ENABLED) ? `https://${trackingUrl}` : `${TRACKING_APP_URL}/?host=${encodeURIComponent(trackingUrl)}`)
    : null;
  const logicalW = device === 'mobile' ? MOBILE_W : DESKTOP_W;
  const frameH = device === 'mobile' ? 720 : 760;
  const scale = wrapW ? Math.min(1, wrapW / logicalW) : (device === 'mobile' ? 1 : 0.6);

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="Design the branded page customers see when tracking their orders">Tracking Page</PageTitle>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Box sx={sectionCardSx}>
              <Typography sx={{ ...cardTitleSx, mb: 1 }}>Your tracking URL</Typography>
              {trackingUrl ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography component="a" href={publicUrl} target="_blank" rel="noreferrer"
                    sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: PRIMARY_BLUE, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    {publicUrl}
                  </Typography>
                  {status && (
                    <Chip size="small" label={status} color={status === 'LIVE' ? 'success' : 'default'} />
                  )}
                </Box>
              ) : (
                <Typography sx={hintSx}>
                  Will be assigned by Proship after you submit your branding.
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={sectionCardSx}>
              <Box sx={sectionHeaderSx}>
                <Box sx={numBadgeSx(PRIMARY_BLUE)}>1</Box>
                <Typography sx={cardTitleSx}>Brand identity</Typography>
                <Typography component="span" sx={hintSx}>* required</Typography>
              </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <RHFTextField name="brandName" label="Brand name" mandatory />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFTextField name="webUrl" label="Brand website URL" placeholder="https://yourbrand.com" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFTextField name="bannerBgColor" label="Accent / banner colour" placeholder="#1D9E75" />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <UploadedThumb label="Current logo" url={logoUrl} height={36} onRemove={() => { setLogoUrl(''); setValue('file', null); }} />
                    {/* Raster only: SVG needs an image/svg+xml Content-Type to render in <img>, which the upload
    endpoint doesn't set — PNG/JPG are content-sniffed by browsers and always render. */}
                    <RHFFileField name="file" label="Brand logo" fileType={['.png', '.jpg', '.jpeg']}
                    helperText="Allowed: .png, .jpg, .jpeg — recommended 360×120px or larger, transparent PNG looks best" />
                    {uploading.logo && <Typography sx={hintSx}>Uploading logo…</Typography>}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <UploadedThumb label="Current desktop banner" url={bannerLargeUrl} height={36} onRemove={() => { setBannerLargeUrl(''); setValue('bannerDesktopFile', null); }} />
                    <RHFFileField name="bannerDesktopFile" label="Banner — desktop" fileType={['.png', '.jpg', '.jpeg']}
                    helperText="Allowed: .png, .jpg, .jpeg — recommended 1920×320px (6:1 strip)" />
                    {uploading.large && <Typography sx={hintSx}>Uploading…</Typography>}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <UploadedThumb label="Current mobile banner" url={bannerSmallUrl} height={36} onRemove={() => { setBannerSmallUrl(''); setValue('bannerMobileFile', null); }} />
                    <RHFFileField name="bannerMobileFile" label="Banner — mobile" fileType={['.png', '.jpg', '.jpeg']}
                    helperText="Allowed: .png, .jpg, .jpeg — recommended 750×375px (2:1)" />
                    {uploading.small && <Typography sx={hintSx}>Uploading…</Typography>}
                  </Grid>
                </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={sectionCardSx}>
              <Box sx={sectionHeaderSx}>
                <Box sx={numBadgeSx('#283593')}>2</Box>
                <Typography sx={cardTitleSx}>Footer &amp; links</Typography>
                <Box component="span" sx={optionalTagSx}>Optional</Box>
              </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <RHFTextArea name="footer" label="Footer note" rows={2} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField name="whatsappNumber" label="Support WhatsApp number" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField name="instaUrl" label="Instagram URL" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField name="fbUrl" label="Facebook URL" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField name="twitterUrl" label="X / Twitter URL" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField name="linkedinUrl" label="LinkedIn URL" />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFTextField name="termsAndConditions" label="Terms &amp; Conditions URL" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFTextField name="returnPolicy" label="Return Policy URL" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFTextField name="privacyPolicy" label="Privacy Policy URL" />
                  </Grid>
                </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={sectionCardSx}>
              <Box sx={{ ...sectionHeaderSx, mb: 0.5 }}>
                <Box sx={numBadgeSx('#E65100')}>3</Box>
                <Typography sx={cardTitleSx}>Choose a design &amp; preview</Typography>
                <Box component="span" sx={requiredTagSx}>Required</Box>
              </Box>
              <Typography sx={{ ...hintSx, display: 'block', mb: 1.5 }}>
                Pick a design — the preview below updates instantly so you can compare them.
              </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {TEMPLATE_CARDS.map((t) => {
                    const selected = designTemplate === t.value;
                    return (
                      <Grid item xs={12} sm={4} key={t.value}>
                        <Box
                          onClick={() => setValue('designTemplate', t.value, { shouldDirty: true })}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: WHITE,
                            border: selected ? `2px solid ${PRIMARY_BLUE}` : `1px solid ${BORDER}`,
                            borderRadius: '10px',
                            boxShadow: selected ? SHADOW.cardHover : SHADOW.card,
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'scale(1.01)', boxShadow: SHADOW.cardHover },
                            p: 1.5,
                            height: '100%',
                          }}
                        >
                          <Box sx={{ height: 80, bgcolor: 'grey.50', borderRadius: 1, p: 1, mb: 1, overflow: 'hidden' }}>
                            {t.value === 'CLASSIC' && (
                              <Box>
                                <Box sx={{ height: 12, bgcolor: 'primary.main', borderRadius: 0.5, mb: 0.75 }} />
                                {[0, 1, 2].map((i) => (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400' }} />
                                    <Box sx={{ flex: 1, height: 5, bgcolor: 'grey.300', borderRadius: 0.5 }} />
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {t.value === 'MODERN' && (
                              <Box>
                                <Box sx={{ width: '55%', height: 8, bgcolor: 'primary.main', borderRadius: 0.5, mb: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                                  {[0, 1, 2, 3].map((i) => (
                                    <React.Fragment key={i}>
                                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: i <= 1 ? 'primary.main' : 'grey.300' }} />
                                      {i < 3 && <Box sx={{ flex: 1, height: 2, bgcolor: i < 1 ? 'primary.main' : 'grey.300' }} />}
                                    </React.Fragment>
                                  ))}
                                </Box>
                                {[0, 1].map((i) => (
                                  <Box key={i} sx={{ height: 5, bgcolor: 'grey.300', borderRadius: 0.5, mb: 0.5 }} />
                                ))}
                              </Box>
                            )}
                            {t.value === 'MINIMAL' && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 0.75 }}>
                                <Box sx={{ width: '40%', height: 6, bgcolor: 'grey.400', borderRadius: 0.5 }} />
                                <Box sx={{ width: '70%', height: 5, bgcolor: 'grey.300', borderRadius: 0.5 }} />
                                <Box sx={{ width: '55%', height: 5, bgcolor: 'grey.300', borderRadius: 0.5 }} />
                              </Box>
                            )}
                          </Box>
                          <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>{t.label}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>Preview</Typography>
                    <Chip
                      label="Desktop" size="small"
                      color={device === 'desktop' ? 'primary' : 'default'}
                      variant={device === 'desktop' ? 'filled' : 'outlined'}
                      onClick={() => setDevice('desktop')}
                    />
                    <Chip
                      label="Mobile" size="small"
                      color={device === 'mobile' ? 'primary' : 'default'}
                      variant={device === 'mobile' ? 'filled' : 'outlined'}
                      onClick={() => setDevice('mobile')}
                    />
                  </Stack>
                  <Button size="small" sx={secondaryBtnSx} onClick={() => setPreviewSrc(buildPreviewUrl())}>
                    Refresh preview
                  </Button>
                </Box>
                <Box
                  ref={previewWrapRef}
                  sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', bgcolor: 'grey.100', p: 1 }}
                >
                  <Box sx={{ width: logicalW * scale, height: frameH * scale, mx: device === 'mobile' ? 'auto' : 0, position: 'relative' }}>
                    <iframe
                      title="Tracking page preview"
                      src={previewSrc}
                      style={{
                        width: logicalW,
                        height: frameH,
                        border: 0,
                        display: 'block',
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                      }}
                    />
                  </Box>
                </Box>
                <Typography sx={{ ...hintSx, display: 'block', mt: 1 }}>
                  Live sample with your branding and the selected design. Text edits (name, colour, footer)
                  appear after you click “Refresh preview”.
                </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <LoadingButton type="submit" variant="contained" loading={loading} disabled={anyUploading} sx={primaryBtnSx}>
              Save tracking page
            </LoadingButton>
          </Grid>
        </Grid>
      <ImageCropDialog
        open={!!pendingCrop}
        file={pendingCrop ? pendingCrop.file : null}
        aspect={pendingCrop ? SLOTS[pendingCrop.slot].aspect : undefined}
        outWidth={pendingCrop ? SLOTS[pendingCrop.slot].outWidth : undefined}
        minWidth={pendingCrop ? SLOTS[pendingCrop.slot].minWidth : 0}
        minHeight={pendingCrop ? SLOTS[pendingCrop.slot].minHeight : 0}
        title={pendingCrop ? SLOTS[pendingCrop.slot].title : ''}
        hint={pendingCrop ? SLOTS[pendingCrop.slot].hint : ''}
        onCancel={closeCrop}
        onConfirm={onCropped}
      />
      </FormProvider>
    </Box>
  );
};

export default withLayout(TrackingConfig);
